import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Upload,
  Trash2,
  Mail,
  UserPlus,
  Pencil,
  Search,
  X,
  Check,
} from "lucide-react";
import PortalShell from "@/components/PortalShell";
import { portalApi, portalMe } from "@/lib/portalApi";

const SOURCE_LABEL = {
  csv: "CSV",
  manual: "Added",
  ticket_purchase: "Ticket buyer",
};

export default function PortalContacts() {
  const [me, setMe] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [meRes, cRes] = await Promise.all([portalMe(), portalApi.get("/portal/contacts")]);
      setMe(meRes);
      setContacts(cRes.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const upload = async (f) => {
    if (!f || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await portalApi.post("/portal/contacts/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted}${data.skipped ? ` · ${data.skipped} skipped` : ""}.`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Import failed.";
      toast.error(typeof detail === "string" ? detail : "Import failed.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await portalApi.delete(`/portal/contacts/${id}`);
      setContacts((cs) => cs.filter((c) => c.id !== id));
      toast.success("Contact deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const filtered = contacts.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [
      c.email,
      c.first_name,
      c.last_name,
      c.phone,
    ]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <PortalShell me={me} testId="portal-contacts-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-8 py-10">
        {/* Header */}
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-3">Email List</div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
              {loading ? "Loading…" : `${contacts.length} contact${contacts.length === 1 ? "" : "s"}`}
            </h1>
            <p className="mt-3 text-sm text-[#A3A3A3] max-w-xl">
              These contacts receive your weekly "What's On" email every Monday. Ticket buyers
              are added automatically.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="portal-contacts-upload"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 border border-white/20 hover:border-[#D9A6F5] hover:text-[#D9A6F5] px-5 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors disabled:opacity-60"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploading ? "Uploading…" : "Upload CSV"}
            </button>
            <button
              type="button"
              data-testid="portal-contacts-add-open"
              onClick={() => {
                setShowAdd(true);
                setEditingId(null);
              }}
              className="inline-flex items-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] px-6 py-3 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <UserPlus size={13} /> Add contact
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="portal-contacts-file"
              onChange={(e) => upload(e.target.files?.[0] || null)}
            />
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
          <input
            type="text"
            placeholder="Search by name, email or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="portal-contacts-search"
            className="w-full bg-black border border-white/15 h-11 pl-10 pr-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]"
          />
        </div>

        {/* Add form */}
        {showAdd && (
          <ContactForm
            mode="create"
            onCancel={() => setShowAdd(false)}
            onSaved={async () => {
              setShowAdd(false);
              await load();
              toast.success("Contact added.");
            }}
          />
        )}

        {loading ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 size={20} className="animate-spin text-[#D9A6F5]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#A3A3A3]">
            {contacts.length === 0
              ? "No contacts yet. Add one or upload a CSV to get started."
              : "No contacts match your search."}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-white/10">
            <div className="hidden md:grid grid-cols-[1.6fr_1.4fr_1fr_1fr_100px_60px] gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.22em] text-[#D9A6F5]">
              <div>Email</div>
              <div>Name</div>
              <div>Phone</div>
              <div>Source</div>
              <div></div>
              <div></div>
            </div>
            {filtered.map((c) =>
              editingId === c.id ? (
                <ContactForm
                  key={c.id}
                  mode="edit"
                  contact={c}
                  onCancel={() => setEditingId(null)}
                  onSaved={async () => {
                    setEditingId(null);
                    await load();
                    toast.success("Contact updated.");
                  }}
                />
              ) : (
                <div
                  key={c.id}
                  data-testid={`contact-row-${c.id}`}
                  className="grid md:grid-cols-[1.6fr_1.4fr_1fr_1fr_100px_60px] grid-cols-1 gap-2 md:gap-4 px-5 py-3 border-b border-white/5 last:border-b-0 text-sm items-center group"
                >
                  <div className="text-[#F5F5F0] truncate flex items-center gap-2">
                    <Mail size={12} className="text-[#D9A6F5]" />
                    <span data-testid={`contact-email-${c.id}`}>{c.email}</span>
                  </div>
                  <div className="text-[#A3A3A3] truncate">
                    {(c.first_name || "") + " " + (c.last_name || "") || "—"}
                  </div>
                  <div className="text-[#A3A3A3] truncate">{c.phone || "—"}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3]">
                    {SOURCE_LABEL[c.source] || (c.source || "—")}
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      data-testid={`contact-edit-${c.id}`}
                      onClick={() => {
                        setEditingId(c.id);
                        setShowAdd(false);
                      }}
                      className="inline-flex items-center gap-1 text-[#A3A3A3] hover:text-[#D9A6F5] text-[10px] uppercase tracking-[0.2em] font-bold"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      onClick={() => remove(c.id)}
                      data-testid={`contact-delete-${c.id}`}
                      className="text-[#A3A3A3] hover:text-red-400 p-1"
                      aria-label="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </PortalShell>
  );
}

function ContactForm({ mode, contact, onCancel, onSaved }) {
  const [f, setF] = useState({
    email: contact?.email || "",
    first_name: contact?.first_name || "",
    last_name: contact?.last_name || "",
    phone: contact?.phone || "",
  });
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        email: f.email.trim().toLowerCase(),
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        phone: f.phone.trim(),
      };
      if (isEdit) {
        await portalApi.patch(`/portal/contacts/${contact.id}`, payload);
      } else {
        await portalApi.post("/portal/contacts", payload);
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Save failed.";
      toast.error(typeof detail === "string" ? detail : "Save failed.");
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      data-testid={isEdit ? `contact-edit-form-${contact.id}` : "contact-create-form"}
      className={`grid grid-cols-1 md:grid-cols-[1.6fr_1.4fr_1fr_1fr_100px_60px] gap-2 md:gap-3 px-5 py-4 ${
        isEdit ? "border-b border-white/10 bg-[#111]" : "border border-white/15 bg-[#0A0A0A] mb-4"
      }`}
    >
      <input
        required
        type="email"
        placeholder="email@example.com"
        value={f.email}
        onChange={(e) => setF({ ...f, email: e.target.value })}
        data-testid="contact-form-email"
        className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          placeholder="First"
          value={f.first_name}
          onChange={(e) => setF({ ...f, first_name: e.target.value })}
          data-testid="contact-form-first-name"
          className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]"
        />
        <input
          type="text"
          placeholder="Last"
          value={f.last_name}
          onChange={(e) => setF({ ...f, last_name: e.target.value })}
          data-testid="contact-form-last-name"
          className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]"
        />
      </div>
      <input
        type="tel"
        placeholder="Phone"
        value={f.phone}
        onChange={(e) => setF({ ...f, phone: e.target.value })}
        data-testid="contact-form-phone"
        className="bg-black border border-white/15 h-10 px-3 text-[#F5F5F0] text-sm focus:outline-none focus:border-[#D9A6F5]"
      />
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A3A3A3] self-center">
        {isEdit ? "Editing" : "New"}
      </div>
      <button
        type="submit"
        disabled={saving}
        data-testid="contact-form-save"
        className="inline-flex items-center justify-center gap-1 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:opacity-60 h-10 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors"
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
        Save
      </button>
      <button
        type="button"
        onClick={onCancel}
        data-testid="contact-form-cancel"
        className="inline-flex items-center justify-center gap-1 border border-white/15 hover:border-white/40 h-10 text-[10px] uppercase tracking-[0.22em] font-bold text-[#A3A3A3] hover:text-[#F5F5F0]"
      >
        <X size={12} /> Cancel
      </button>
    </form>
  );
}
